import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import {
  Button,
  PasswordInput,
  Text,
  TextInput,
  Alert,
  Anchor,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { IconCircleCheck } from '@tabler/icons-react'
import {
  LoginForm,
  LoginFormSchema,
  LoginFormWithToken,
  LoginFormWithTokenSchema,
  LoginResponseTwoFactorAuthentication,
  LoginSuccessResponse,
  useLogin,
  useLoginWithToken,
} from './api'
import { getCookie } from '../axios-client'
import AuthLayout from './AuthLayout'
import { getDefaults } from '../common/helpers'
import Meta from '../common/Meta'
import Footer from '../common/components/footer/footer'
import { ReactComponent as LeaguestarLogo } from '../assets/leaguestar_logo.svg'
import { registerStoredPushToken } from '../mobile-notifications'
import classes from './Auth.module.css'
import clsx from 'clsx'

const DEFAULT_REDIRECT = '/'

type LoginAPIResponse =
  | LoginSuccessResponse
  | LoginResponseTwoFactorAuthentication

const is2FARequired = (
  data: LoginAPIResponse
): data is LoginResponseTwoFactorAuthentication => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'code' in data &&
    data.code === 'otp_required'
  )
}

export default function LoginPage() {
  const [searchParams] = useSearchParams()

  const defaults =
    process.env.NODE_ENV === 'development'
      ? { email: 'demo@amp.hr', password: 'demo' }
      : getDefaults(LoginFormSchema)

  const initialValues: LoginForm = {
    email: searchParams.get('u') || defaults.email,
    password: searchParams.get('p') || defaults.password,
  }

  const form = useForm<LoginForm>({
    initialValues,
    validate: zodResolver(LoginFormSchema),
  })

  const tokenForm = useForm<LoginFormWithToken>({
    initialValues: {
      email: '',
      password: '',
      token: '',
    },
    validate: zodResolver(LoginFormWithTokenSchema),
  })

  const navigate = useNavigate()
  const { mutate: login, isPending, isError, error } = useLogin()
  const redirect = searchParams.get('redirect')
  const passwordReset = searchParams.get('passwordReset') === 'true'
  const isLoggedIn = getCookie('leaguestar-logged-in-cookie')
  const [showTokenForm, setShowTokenForm] = useState<boolean>(false)
  const {
    mutate: loginWithToken,
    isPending: isLoginWithTokenPending,
    isError: isLoginWithTokenError,
    error: loginWithTokenError,
  } = useLoginWithToken()

  useEffect(() => {
    if (isLoggedIn && searchParams.get('force') !== 'true') {
      navigate(redirect || DEFAULT_REDIRECT)
    }
  }, [isLoggedIn, navigate])

  if (isLoggedIn && searchParams.get('force') !== 'true') return null

  const handleLogin = (data: LoginForm) => {
    login(data, {
      onSuccess: async (responseData) => {
        if (is2FARequired(responseData)) {
          setShowTokenForm(true)
          tokenForm.setValues({
            email: data.email,
            password: data.password,
            token: '',
          })
        } else {
          // Register push token after successful login
          await registerStoredPushToken()
          navigate(redirect || DEFAULT_REDIRECT)
        }
      },
    })
  }

  const handleLoginWithToken = (data: LoginFormWithToken) => {
    loginWithToken(data, {
      onSuccess: async () => {
        // Register push token after successful login
        await registerStoredPushToken()
        navigate(redirect || DEFAULT_REDIRECT)
      },
    })
  }

  return (
    <AuthLayout>
      <Meta title="Login" />
      <div className={classes.auth_container}>
        <div className={classes.auth_wrapper}>
          <LeaguestarLogo className={classes.leaguestar_logo} />
          <div className={classes.form_wrapper_container}>
            <form
              noValidate
              className={clsx(classes.form_container, classes.token_form, {
                [classes.slide_in]: showTokenForm,
              })}
              onSubmit={tokenForm.onSubmit((values) =>
                handleLoginWithToken(values)
              )}
            >
              <div className={classes.form_wrapper}>
                <TextInput
                  placeholder="Token"
                  type="text"
                  classNames={{
                    error: classes.error,
                    input: classes.input,
                    wrapper: classes.wrapper,
                  }}
                  {...tokenForm.getInputProps('token')}
                />

                {isLoginWithTokenError && (
                  <Text className={classes.error}>
                    {loginWithTokenError.message}
                  </Text>
                )}
                <Button
                  className={classes.button}
                  type="submit"
                  disabled={isLoginWithTokenPending}
                  loading={isLoginWithTokenPending}
                >
                  Verify with token
                </Button>
              </div>
            </form>

            <form
              noValidate
              className={clsx(classes.form_container, {
                [classes.slide_out]: showTokenForm,
              })}
              onSubmit={form.onSubmit((values) => handleLogin(values))}
            >
              <div className={classes.form_wrapper}>
                {passwordReset && (
                  <Alert
                    icon={<IconCircleCheck />}
                    color="green"
                    classNames={{
                      root: classes.alert_root,
                      message: classes.info_text,
                      icon: classes.check_icon,
                    }}
                  >
                    Password reset successful! You can now log in with your new
                    password.
                  </Alert>
                )}
                <TextInput
                  placeholder="Email"
                  type="email"
                  classNames={{
                    error: classes.error,
                    input: classes.input,
                    wrapper: classes.wrapper,
                  }}
                  {...form.getInputProps('email')}
                />
                <PasswordInput
                  placeholder="Password"
                  type="password"
                  classNames={{
                    input: classes.password_input,
                    error: classes.error,
                  }}
                  {...form.getInputProps('password')}
                />
                {isError && (
                  <Text className={classes.error}>{error.message}</Text>
                )}
                <Anchor
                  component={Link}
                  to="/forgot-password"
                  className={classes.forgot_password}
                  underline="always"
                >
                  Forgot password?
                </Anchor>
                <Button
                  className={classes.button}
                  type="submit"
                  disabled={isPending}
                  loading={isPending}
                >
                  Log in
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </AuthLayout>
  )
}
